
import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import HeroSection from "@/components/products/HeroSection";
import KeyFeaturesSection from "@/components/products/KeyFeaturesSection";
import AgenticAICapabilitiesSection from "@/components/products/AgenticAICapabilitiesSection";
import SIEMComparisonSection from "@/components/products/SIEMComparisonSection";
import UseCasesSection from "@/components/products/UseCasesSection";
import TestimonialsSection from "@/components/products/TestimonialsSection";
import FAQSection from "@/components/products/FAQSection";
import FeaturedSnippet from "@/components/seo/FeaturedSnippet";
import CanonicalTag from "@/components/seo/CanonicalTag";

const Products = () => {
  React.useEffect(() => {
    // SEO Meta updates for Spharaka Networks
    document.title = "Spharaka Networks XDR™ - AI Powered Extended Detection and Response Platform";
    
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', 'Transform your cybersecurity with Spharaka Networks XDR™. The industry\'s first Agentic AI powered Extended Detection and Response platform that autonomously protects your digital ecosystem.');
    document.head.appendChild(metaDescription);

    return () => {
      try {
        document.head.removeChild(metaDescription);
      } catch (e) {}
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <CanonicalTag path="/products/spharakaxdr" />
      <Navbar />
      <main className="flex-1">
        <HeroSection />
        <KeyFeaturesSection />
        
        {/* Featured Snippet Optimization */}
        <section className="py-16 bg-white" id="what-is-spharaka-xdr">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <FeaturedSnippet
                question="What is Spharaka XDR™?"
                answer="Spharaka XDR™ is the industry's first Agentic AI powered Extended Detection and Response platform that autonomously detects, investigates, and responds to cybersecurity threats across your entire digital ecosystem in real time."
                additionalInfo={[
                  "Deploys in hours, not months",
                  "Reduces false positives by up to 95%",
                  "Operates 24/7 with minimal human oversight",
                  "Integrates with existing security tools"
                ]}
              />

              <h2 className="text-3xl md:text-4xl font-bold mb-8">
                How Does <span className="text-[#8932c8]">AI Powered XDR</span> Work?
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 mb-6">
                  Spharaka XDR™ leverages Agentic AI technology to transform cybersecurity operations from reactive to proactive. Our platform creates a unified intelligence layer that correlates data across endpoints, networks, cloud workloads, and applications, providing comprehensive threat visibility that traditional security tools cannot match.
                </p>
                <p className="text-lg text-gray-700 mb-8">
                  The system autonomously conducts multi-stage investigations, enriches alerts with contextual intelligence, and executes precise responses - all while continuously learning from new threats to improve detection accuracy over time.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Comprehensive Coverage Section with Featured Snippets */}
        <section className="py-16 bg-gray-50" id="security-coverage">
          <div className="container mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <FeaturedSnippet
                question="What does Spharaka XDR™ protect?"
                answer="Spharaka XDR™ provides comprehensive security coverage across endpoints, networks, cloud workloads, applications, and user identities through a single unified platform."
                additionalInfo={[
                  "Endpoint protection for all device types",
                  "Network traffic analysis and monitoring",
                  "Cloud native security for containers and serverless",
                  "Application-level threat detection"
                ]}
              />

              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center">
                End to End <span className="text-[#8932c8]">Security Coverage</span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <h3 className="text-xl font-bold mb-3" id="endpoint-protection">Endpoint Protection</h3>
                  <p className="text-gray-600">
                    Advanced endpoint detection and response capabilities that protect workstations, servers, and mobile devices from sophisticated attacks including malware, ransomware, and zero-day exploits.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <h3 className="text-xl font-bold mb-3" id="network-security">Network Security</h3>
                  <p className="text-gray-600">
                    Deep packet inspection and network behavior analysis to detect lateral movement, data exfiltration, command and control communications, and advanced persistent threats.
                  </p>
                </div>

                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                  <h3 className="text-xl font-bold mb-3" id="cloud-protection">Cloud Workload Protection</h3>
                  <p className="text-gray-600">
                    Native cloud security that adapts to dynamic environments, protecting containers, serverless functions, cloud native applications, and multi cloud infrastructures.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <AgenticAICapabilitiesSection />
        <SIEMComparisonSection />
        <UseCasesSection />
        <TestimonialsSection />
        <FAQSection />
      </main>
      <Footer />
    </div>
  );
};

export default Products;
